/**
 * 발송에 실을 스레딩 헤더(In-Reply-To/References) 값을 계산하는 순수 로직.
 *
 * SendHelper 의 발송 3경로(수신확인·보안·예약)와 기존 createEmail 이 공유하는 단일 지점이며,
 * 발신자에게는 보이지 않고 수신 측에서만 드러나는 스레딩 회귀를 막기 위해 별도 모듈로 분리해
 * 단위 테스트한다.
 *
 * - 회신(reply/replyAll): In-Reply-To + References 를 모두 싣는다.
 * - 전달(forward): 회신이 아니므로 In-Reply-To 는 생략하고 References 로만 스레드 연속성을 잇는다
 *   (RFC 5322 관례). in_reply_to 는 undefined.
 */

/** 부모 메일에서 뽑아낸 스레딩 계산 입력. JMAP `Email` 의 `messageId`/`references` 속성. */
export interface ThreadingSource {
  messageId?: string[]
  references?: string[]
}

export interface ThreadingFields {
  /** 회신일 때만 존재. forward 는 In-Reply-To 를 싣지 않으므로 undefined. */
  in_reply_to?: string
  references: string[]
}

/** 서버가 References 배열 51개면 422 를 반환하므로 루트 보존 트리밍으로 50개 이내로 맞춘다. */
const MAX_REFERENCES = 50

/**
 * @param cmd 발송 세션의 명령. `reply`/`replyAll` 은 회신으로, `forward` 는 전달로 처리한다.
 * @param source 부모 메일 데이터(`prevEmail.data`).
 * @returns 회신·전달이 아니거나 부모 Message-ID 가 없으면 null.
 */
export const computeThreadingFields = (
  cmd: string | null | undefined,
  source: ThreadingSource | null | undefined,
): ThreadingFields | null => {
  const isReply = cmd === 'reply' || cmd === 'replyAll'
  const isForward = cmd === 'forward'
  if (!isReply && !isForward) return null

  const parent = source?.messageId?.[0]
  if (parent == null || parent.length === 0) return null

  const refs = [...new Set([...(source?.references || []), parent])]
  return {
    // forward 는 회신이 아니므로 In-Reply-To 를 싣지 않는다.
    in_reply_to: isReply ? parent : undefined,
    // 루트(refs[0])는 보존하고 최근 49개만 남겨 50개 이내로 트리밍.
    references:
      refs.length > MAX_REFERENCES ? [refs[0], ...refs.slice(-(MAX_REFERENCES - 1))] : refs,
  }
}
